/**
 * Authoritative page registry — used by both:
 *   • Security Requirements page (renders per-role page toggles for reauthentication)
 *   • useReauthGate hook (looks up whether the current page requires reauth for the user's role)
 *
 * Keys are STABLE — once a page id is published into the
 * `security_policies.reauth.page_toggles` map in Mongo, renaming
 * the id here will silently turn off reauth for that page. Treat
 * these ids as a public contract.
 *
 * `path` may include react-router params (`:id`). The gate hook
 * matches by react-router's resolved route pattern (NOT the
 * concrete url), so admin/users covers both /admin/users and
 * /admin/users/<id>/edit unless we add a more specific id.
 *
 * `roles` lists every role that has nav access to the page. Admin
 * pages also surface in the "special" tab (special users see the
 * admin set) — that mapping is handled in the UI, not here.
 */

// Pages visible to AFFILIATES, LEADS, SUPERVISORS — the standard
// signed-in surface. Admin-only pages live in ADMIN_PAGES below.
export const USER_PAGES = [
  { id: 'home', label: 'Home', path: '/' },
  { id: 'calls', label: 'Calls', path: '/calls' },
  { id: 'calls-history', label: 'Call History', path: '/calls/history' },
  { id: 'chat', label: 'Chat', path: '/chat' },
  { id: 'email', label: 'Email', path: '/email' },
  { id: 'calendar', label: 'Calendar', path: '/calendar' },
  { id: 'tasks', label: 'Tasks', path: '/tasks' },
  { id: 'contacts', label: 'Contacts', path: '/contacts' },
  { id: 'memos', label: 'Memos', path: '/memos' },
  { id: 'messages', label: 'Messages', path: '/messages' },
  { id: 'voicemail', label: 'Voicemail', path: '/voicemail' },
  { id: 'resources', label: 'Resources', path: '/resources' },
  { id: 'settings', label: 'Settings', path: '/settings' },
  { id: 'profile', label: 'Profile', path: '/profile' },
  { id: 'notifications', label: 'Notifications', path: '/notifications' },
  { id: 'business-card', label: 'Business Card', path: '/business-card' },
  { id: 'my-team', label: 'My Team', path: '/my-team' },
  { id: 'my-peers', label: 'My Peers', path: '/my-peers' },
  { id: 'orb-characters', label: 'ORB Characters', path: '/orb-characters' },
  { id: 'feedback', label: 'Feedback', path: '/feedback' },
];

// Pages visible to ADMIN users (and surfaced to SPECIAL users too).
export const ADMIN_PAGES = [
  { id: 'admin-users', label: 'Users', path: '/admin/users' },
  { id: 'admin-master-contacts', label: 'Master Contacts', path: '/admin/master-contacts' },
  { id: 'admin-master-history', label: 'Master History', path: '/admin/master-history' },
  { id: 'admin-memos', label: 'Memos (Admin)', path: '/admin/memos' },
  { id: 'admin-calling', label: 'Calling', path: '/admin/calling' },
  { id: 'admin-phone-numbers', label: 'Phone Numbers', path: '/admin/phone-numbers' },
  { id: 'admin-phone-routing', label: 'Phone Routing', path: '/admin/phone-routing' },
  { id: 'admin-statewide-details', label: 'Statewide Details', path: '/admin/statewide-details' },
  { id: 'admin-restricted-states', label: 'Restricted States', path: '/admin/restricted-states' },
  { id: 'admin-orb-universes', label: 'ORB Universes', path: '/admin/orb-universes' },
  { id: 'admin-onboarding', label: 'Onboarding', path: '/admin/onboarding' },
  { id: 'admin-business-cards', label: 'Business Cards', path: '/admin/business-cards' },
  { id: 'admin-assignments', label: 'Assignments', path: '/admin/assignments' },
  { id: 'admin-feedback', label: 'Feedback (Admin)', path: '/admin/feedback' },
  { id: 'admin-ai-training', label: 'AI Training', path: '/admin/ai-training' },
  { id: 'admin-ai-voice-dashboard', label: 'AI Voice Dashboard', path: '/admin/ai-voice-dashboard' },
  { id: 'admin-user-communications', label: 'User Communications', path: '/admin/user-communications' },
  { id: 'admin-help-buttons', label: 'Help Buttons', path: '/admin/help-buttons' },
  { id: 'admin-recovery-approvals', label: 'Recovery Approvals', path: '/admin/recovery-key-approvals' },
  { id: 'admin-security-requirements', label: 'Security Requirements', path: '/admin/security-requirements' },
  { id: 'admin-email-verifications', label: 'Email Verifications', path: '/admin/email-verifications' },
];

// Map role → page list driving the toggle grid on Security Requirements.
// `special` deliberately mirrors `admin` per spec — special users see
// (a subset of) the admin pages and reauth policy for admin pages
// applies to them too.
export const PAGES_FOR_ROLE = {
  affiliate: USER_PAGES,
  lead: USER_PAGES,
  supervisor: USER_PAGES,
  special: ADMIN_PAGES,
  admin: ADMIN_PAGES,
};

// Reverse lookup: pathname → page id. Used by useReauthGate at runtime
// to determine if the current route needs to enforce reauth.
const ALL_PAGES = [...USER_PAGES, ...ADMIN_PAGES];

export const findPageIdByPath = (pathname) => {
  if (!pathname) return null;
  // Longest path-prefix match — /admin/users/123/edit matches
  // /admin/users; /admin/user-communications/foo matches the
  // user-communications page id (NOT /admin/users).
  let best = null;
  for (const p of ALL_PAGES) {
    if (pathname === p.path) return p.id;
    if (pathname.startsWith(p.path + '/')) {
      if (!best || p.path.length > best.path.length) best = p;
    }
  }
  return best?.id || null;
};
